import { createRouter } from "@tanstack/solid-router";
import { NotFoundRoute } from "@/components/not-found-route";
import { rootRoute } from "@/routes/__root";
import { authRoute } from "@/routes/_auth/route";
import { loginRoute } from "@/routes/_auth/login";
import { registerRoute } from "@/routes/_auth/register";
import { forgotPasswordRoute } from "@/routes/_auth/forgot-password";
import { resetPasswordRoute } from "@/routes/_auth/reset-password";
import { verifyEmailRoute } from "@/routes/_auth/verify-email";
import { socialCallbackRoute } from "@/routes/_auth/social-callback";
import { appRoute } from "@/routes/_app/route";
import { indexRoute } from "@/routes/_app/index";
import { profileRoute } from "@/routes/_app/profile";
import { debugRoute } from "@/routes/_app/debug";
import { organizationsRoute } from "@/routes/_app/organizations";
import { organizationDetailRoute } from "@/routes/_app/organizations.$orgId";
import { invitationsRoute } from "@/routes/_app/invitations";
import { themeGalleryRoute } from "@/routes/_app/themes";
import { languageRoute } from "@/routes/_app/language";
import { profileFontsRoute } from "@/routes/_app/profile.fonts";
import { profileShortcutsRoute } from "@/routes/_app/profile.shortcuts";
import { profileDeleteAccountRoute } from "@/routes/_app/profile.delete-account";
import { profileTransferSendRoute } from "@/routes/_app/profile.transfer-send";
import { profileTransferReceiveRoute } from "@/routes/_app/profile.transfer-receive";
import { productsRoute } from "@/routes/_app/products";
import { warehousesRoute } from "@/routes/_app/warehouses";
import { cc21ProbeRoute } from "@/routes/_app/cc21-probe";
import { stockRoute } from "@/routes/_app/stock";
import { operationsRoute } from "@/routes/_app/operations";
import { replenishmentRoute } from "@/routes/_app/replenishment";
import { purchasesRoute } from "@/routes/_app/purchases";
import { valuationRoute } from "@/routes/_app/valuation";
import { lotsRoute } from "@/routes/_app/lots";
import { countsRoute } from "@/routes/_app/counts";
import { manufacturingRoute } from "@/routes/_app/manufacturing";
import { pluginRoutes } from "@/composition";
import { achievementsRoute } from "@/routes/_app/achievements";
import { trashRoute } from "@/routes/_app/trash";

const routeTree = rootRoute.addChildren([
  socialCallbackRoute,
  authRoute.addChildren([
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
  ]),
  appRoute.addChildren([
    // Regression fixture CC-21 (Select/Popover reachable within a modal Dialog).
    // DEV only: it serves cc21-probe.spec and is NOT registered in production.
    ...(import.meta.env.DEV ? [cc21ProbeRoute] : []),
    indexRoute, // path: "/" — Dashboard
    productsRoute, // path: "/products" — Catalog (Stockpilot F1)
    warehousesRoute, // path: "/warehouses" — Warehouses + locations
    stockRoute, // path: "/stock" — Stock on hand + inventory adjustments
    operationsRoute, // path: "/operations" — Receipt/delivery/transfer (F2)
    replenishmentRoute, // path: "/replenishment" — Rules + suggestions (F2)
    purchasesRoute, // path: "/purchases" — Vendors + purchase orders (F3)
    valuationRoute, // path: "/valuation" — AVCO inventory valuation (F3)
    lotsRoute, // path: "/lots" — Lots/serials + expiration (F4)
    countsRoute, // path: "/counts" — Cycle count / physical inventory (F4)
    manufacturingRoute, // path: "/manufacturing" — BoM + orders + landed costs + returns (F5)
    ...pluginRoutes, // path: "/activity" — Feed "who did what" (ACTIVITY-CORE L3)
    achievementsRoute, // path: "/achievements" — XP/streak/badges (GAMIFICATION-CORE)
    trashRoute, // path: "/trash" — Restorable trash (ACTIVITY-CORE L2)
    organizationsRoute,
    organizationDetailRoute,
    invitationsRoute,
    themeGalleryRoute,
    languageRoute,
    profileRoute,
    profileFontsRoute,
    profileShortcutsRoute,
    profileDeleteAccountRoute,
    profileTransferSendRoute,
    profileTransferReceiveRoute,
    debugRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  // Any unknown URL shows a "not found" state with an action to
  // return home, instead of a blank screen.
  defaultNotFoundComponent: NotFoundRoute,
  // "intent" preloads only when the user expresses intent (hover/focus on a
  // <Link>). The previous "viewport" setting eagerly fired `beforeLoad` and
  // PowerSync `resolveTables` EXPLAIN queries for ~20 sidebar routes the
  // moment the dashboard mounted. Combined with PowerSync's initial-sync
  // batches (each one re-invalidating subscribed queries), this saturated
  // the main thread for several seconds after login. Hover-only is the
  // standard tradeoff: a tiny first-hover latency in exchange for a calm
  // post-login render.
  defaultPreload: "intent",
  // oxlint-disable-next-line eslint(no-magic-numbers)
  defaultPreloadStaleTime: 30_000, // 30 s — don't re-preload the same route within 30 s
});

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}
